import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Minus, Plus, Loader2, ShoppingBag, Zap, Heart, Share2,
  ShieldCheck, FlaskConical, FileCheck2, Truck, Lock, Snowflake, Boxes, PackageCheck,
  ChevronRight, BadgeCheck, CircleCheck,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import FrequentlyBought from "../components/FrequentlyBought";
import RecommendationRail from "../components/RecommendationRail";
import RecentlyViewed from "../components/RecentlyViewed";
import Reveal from "../components/Reveal";
import StarRating from "../components/StarRating";
import { api, fmtPrice, effectivePrice } from "../lib/api";
import { handleImgError } from "../lib/imgFallback";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { addRecentlyViewed } from "../lib/recentlyViewed";
import { toast } from "sonner";
import {
  productRating, reviewCount, generateReviews, ratingBreakdown, sellingPoints,
  featureBenefits, qualitySteps, whyChoose, generateFaqs, applyProductSeo,
} from "../lib/productContent";

const ICONS = { ShieldCheck, FlaskConical, FileCheck2, Truck, Lock, Snowflake, Boxes, PackageCheck };
const Icon = ({ name, ...p }) => {
  const C = ICONS[name] || CircleCheck;
  return <C {...p} />;
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [wished, setWished] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setQty(1);
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then(({ data }) => { setProduct(data); addRecentlyViewed(data); }).catch(() => setProduct(null)).finally(() => setLoading(false));
    api.get(`/products/${slug}/related`).then(({ data }) => setRelated(data)).catch(() => setRelated([]));
  }, [slug]);

  const rating = product ? productRating(product.slug) : 0;
  const rcount = product ? reviewCount(product.slug) : 0;
  const reviews = product ? generateReviews(product.slug, 6) : [];
  const breakdown = product ? ratingBreakdown(product.slug, rating, rcount) : [];
  const faqs = product ? generateFaqs(product) : [];
  const points = sellingPoints();
  const fb = featureBenefits();
  const quality = qualitySteps();
  const reasons = whyChoose();

  useEffect(() => {
    if (!product) return;
    const cleanup = applyProductSeo({ product, rating, count: rcount, faqs });
    return cleanup;
  }, [product]);

  useEffect(() => {
    if (!user || !user.user_id) { setWished(false); return; }
    api.get("/account/wishlist").then(({ data }) => setWished(data.some((p) => p.slug === slug))).catch(() => {});
  }, [user, slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4ADE80]" /></div>;
  }
  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-display font-black text-3xl uppercase">Compound not found</p>
        <Link to="/products" className="font-mono-accent text-sm text-[#4ADE80] link-underline">Back to catalog</Link>
      </div>
    );
  }

  const tiers = product.price_tiers || [];
  const unit = effectivePrice(product.price, tiers, qty);
  const discounted = unit < product.price;
  const lineTotal = unit * qty;

  const toggleWishlist = async () => {
    if (!user || !user.user_id) {
      toast.error("Sign in to save products to your wishlist.");
      navigate("/login");
      return;
    }
    try {
      if (wished) { await api.delete(`/account/wishlist/${slug}`); setWished(false); toast.success("Removed from wishlist"); }
      else { await api.post(`/account/wishlist/${slug}`); setWished(true); toast.success("Saved to wishlist"); }
    } catch { toast.error("Could not update wishlist."); }
  };

  const addToCart = () => { addItem(product, qty); };
  const buyNow = () => { addItem(product, qty); navigate("/checkout"); };
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: product.name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard."); }
    } catch { /* dismissed */ }
  };

  const specs = [
    { label: "Product Name", value: product.name },
    { label: "Category", value: product.category },
    { label: "Purity", value: product.purity },
    { label: "Molecular Weight", value: product.molecular_weight },
    { label: "Grade", value: "Pharmaceutical Grade" },
    { label: "Size", value: product.size },
    { label: "Storage", value: "Cold, protected from light" },
    { label: "Shipping", value: "Cold-chain, discreet" },
    { label: "Packaging", value: "Lyophilized, sealed vial" },
    { label: "Country of Fulfillment", value: "Domestic" },
    { label: "Batch Tested", value: "Yes — every lot" },
    { label: "COA Available", value: "Yes" },
  ].filter((s) => s.value && String(s.value).trim());

  return (
    <div className="pt-16">
      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-6">
        <nav className="flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a]" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/products" className="hover:text-white transition-colors">Catalog</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-[#E5E5E5]">{product.name}</span>
        </nav>
      </div>

      {/* HERO */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.99 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            ref={imgRef}
            className="relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0c]"
          >
            <img
              data-testid="product-detail-image" src={product.image} alt={product.name}
              onError={handleImgError}
              className="h-full w-full object-cover"
            />
            <span className="absolute top-4 left-4 font-mono-accent text-[10px] uppercase tracking-widest bg-[#0F0F10]/80 backdrop-blur px-3 py-1.5 text-[#4ADE80] rounded-full">{product.category}</span>
            <span className="absolute bottom-4 left-4 font-mono-accent text-[10px] uppercase tracking-widest bg-[#0F0F10]/80 backdrop-blur px-3 py-1.5 text-[#F5F5F5] rounded-full flex items-center gap-1.5">
              <BadgeCheck className="h-3.5 w-3.5 text-[#4ADE80]" /> {product.purity || "Verified"} Purity
            </span>
          </motion.div>
          <div className="mt-4 flex gap-3">
            {[product.image].map((src, i) => (
              <div key={i} className="h-20 w-20 rounded-xl overflow-hidden border-2 border-[#4ADE80]">
                <img src={src} alt="" onError={handleImgError} className="h-full w-full object-cover" />
              </div>
            ))}
            <div className="h-20 flex-1 rounded-xl border border-white/10 bg-[#1F1F1F] flex items-center justify-center font-mono-accent text-[10px] uppercase tracking-widest text-[#7a7a7a] gap-2">
              <FileCheck2 className="h-4 w-4 text-[#4ADE80]" /> COA on file
            </div>
          </div>
        </div>

        {/* Buy box */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#7a7a7a] mb-3">{product.tagline}</p>
          <h1 data-testid="product-detail-name" className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-[0.9] uppercase">{product.name}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <StarRating value={rating} size={18} />
            <span className="font-mono-accent text-sm text-[#E5E5E5]">{rating.toFixed(1)}</span>
            <a href="#reviews" className="font-mono-accent text-xs text-[#7a7a7a] hover:text-[#4ADE80] transition-colors link-underline">{rcount} verified reviews</a>
          </div>

          <p className="mt-6 text-[#E5E5E5] text-lg leading-relaxed">{product.description}</p>

          <div className="mt-6 flex items-baseline gap-3">
            <span data-testid="product-detail-price" className="font-display font-black text-4xl text-[#4ADE80]">{fmtPrice(unit)}</span>
            {discounted && <span className="font-mono-accent text-base text-[#7a7a7a] line-through">{fmtPrice(product.price)}</span>}
            {discounted && <span className="font-mono-accent text-[10px] uppercase tracking-widest bg-[#4ADE80] text-[#F5F5F5] px-2 py-0.5 rounded-full">Bulk price</span>}
            <span className="font-mono-accent text-xs text-[#7a7a7a]">/ {product.size}</span>
          </div>

          {/* Stock + shipping */}
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-accent text-xs">
            <span data-testid="stock-indicator" className="flex items-center gap-2 text-[#4ADE80]"><span className="h-2 w-2 rounded-full bg-[#4ADE80] animate-pulse" /> {typeof product.stock === "number" && product.stock <= 10 ? `Only ${product.stock} left — order soon` : "In Stock — ready to ship"}</span>
            <span className="flex items-center gap-2 text-[#A8A8A8]"><Truck className="h-4 w-4" /> Ships within 1 business day</span>
          </div>

          {/* Quantity + CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <div className="flex items-center border border-white/10 rounded-full">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-4 hover:text-[#4ADE80] transition-colors"><Minus className="h-4 w-4" /></button>
              <span data-testid="qty-value" className="px-6 font-mono-accent">{qty}</span>
              <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="p-4 hover:text-[#4ADE80] transition-colors"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="detail-add-to-cart" onClick={addToCart} className="flex-1 flex items-center justify-center gap-2 border border-[#4ADE80] text-[#4ADE80] font-display font-bold uppercase tracking-tight py-4 rounded-full hover:bg-[#6FE998] hover:text-[#F5F5F5] transition-colors">
              <ShoppingBag className="h-4 w-4" /> Add to Cart · {fmtPrice(lineTotal)}
            </button>
          </div>
          <button data-testid="buy-now-btn" onClick={buyNow} className="mt-4 w-full flex items-center justify-center gap-2 btn-press bg-[#4ADE80] text-[#0F0F10] font-medium rounded-full py-4 rounded-full hover:bg-[#6FE998] hover:shadow-lg hover:shadow-[#4ADE80]/20 transition-[background-color,box-shadow]">
            <Zap className="h-4 w-4" /> Buy Now
          </button>

          <div className="mt-4 flex items-center gap-6 font-mono-accent text-[11px] uppercase tracking-widest text-[#A8A8A8]">
            <button data-testid="wishlist-btn" onClick={toggleWishlist} className="flex items-center gap-2 hover:text-[#4ADE80] transition-colors">
              <Heart className={`h-4 w-4 ${wished ? "fill-[#4ADE80] text-[#4ADE80]" : ""}`} /> {wished ? "Saved" : "Wishlist"}
            </button>
            <button data-testid="share-btn" onClick={share} className="flex items-center gap-2 hover:text-[#4ADE80] transition-colors"><Share2 className="h-4 w-4" /> Share</button>
          </div>

          {/* Trust badges */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {points.slice(0, 4).map((p) => (
              <div key={p.title} className="border border-white/10 rounded-xl p-3 text-center">
                <Icon name={p.icon} className="h-5 w-5 text-[#4ADE80] mx-auto" strokeWidth={1.5} />
                <p className="mt-2 font-mono-accent text-[10px] uppercase tracking-widest text-[#E5E5E5] leading-tight">{p.title}</p>
              </div>
            ))}
          </div>

          {/* Volume pricing */}
          {tiers.length > 0 && (
            <div className="mt-6 border border-white/10 rounded-xl overflow-hidden" data-testid="tier-pricing">
              <div className="px-5 py-3 border-b border-white/10 font-mono-accent text-[11px] uppercase tracking-widest text-[#A8A8A8] flex items-center justify-between">
                <span>Volume Pricing</span><span className="text-[#7a7a7a]">per vial</span>
              </div>
              <div className="divide-y divide-white/10">
                <div className="flex justify-between px-5 py-3 font-mono-accent text-sm"><span className="text-[#A8A8A8]">1 – {tiers[0].min_qty - 1}</span><span>{fmtPrice(product.price)}</span></div>
                {tiers.map((t, idx) => {
                  const upper = tiers[idx + 1] ? tiers[idx + 1].min_qty - 1 : null;
                  const active = unit === t.price && qty >= t.min_qty;
                  return (
                    <div key={t.min_qty} className={`flex justify-between px-5 py-3 font-mono-accent text-sm ${active ? "bg-[#4ADE80]/10" : ""}`}>
                      <span className="text-[#A8A8A8]">{t.min_qty}{upper ? ` – ${upper}` : "+"}</span>
                      <span className={active ? "text-[#4ADE80]" : ""}>{fmtPrice(t.price)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </section>

      {/* KEY SELLING POINTS */}
      <section className="border-y border-white/10 bg-[#1F1F1F]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {points.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05} className="text-center">
              <div className="h-12 w-12 rounded-full border border-white/10 flex items-center justify-center mx-auto"><Icon name={p.icon} className="h-5 w-5 text-[#4ADE80]" strokeWidth={1.5} /></div>
              <p className="mt-4 font-display font-bold text-sm uppercase tracking-tight">{p.title}</p>
              <p className="mt-1 font-mono-accent text-[11px] text-[#7a7a7a]">{p.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="mx-auto max-w-[1000px] px-5 sm:px-8 py-24 text-center">
        <Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Product Overview</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none">Engineered for Confident Research</h2>
          <p className="mt-8 text-[#E5E5E5] text-lg leading-relaxed">
            {product.name} is a {product.purity || "high-purity"} pharmaceutical-grade {product.tagline.toLowerCase()} supplied for laboratory investigation. Researchers choose it for reproducible quality: every lot is synthesized to defined specification, confirmed by mass spectrometry, and verified by reverse-phase HPLC before release.
          </p>
          <p className="mt-5 text-[#A8A8A8] text-lg leading-relaxed">
            What sets Enda G1 apart is transparency — independent third-party testing, batch-matched certificates of analysis, and lot traceability on every vial. Combined with cold-chain handling and discreet, fast domestic fulfillment, it&apos;s a source labs return to order after order.
          </p>
        </Reveal>
      </section>

      {/* FEATURES & BENEFITS */}
      <section className="border-t border-white/10 bg-[#1F1F1F]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24">
          <Reveal><p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Features &amp; Benefits</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter uppercase leading-none">Built On Quality</h2></Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F1F1F] border border-white/10 rounded-2xl overflow-hidden">
            {fb.map((f, i) => (
              <Reveal key={f.feature} delay={(i % 2) * 0.06} className="bg-[#0F0F10] p-8 flex gap-5 hover:bg-[#151517] transition-colors">
                <div className="h-12 w-12 shrink-0 rounded-full bg-[#4ADE80]/10 flex items-center justify-center"><Icon name={f.icon} className="h-5 w-5 text-[#4ADE80]" strokeWidth={1.6} /></div>
                <div>
                  <h3 className="font-display font-bold text-xl uppercase tracking-tight">{f.feature}</h3>
                  <p className="mt-2 text-[#A8A8A8] leading-relaxed">{f.benefit}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPECIFICATIONS */}
      <section className="mx-auto max-w-[1000px] px-5 sm:px-8 py-24">
        <Reveal><p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Specifications</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none">Technical Detail</h2></Reveal>
        <div className="mt-10 border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/10">
          {specs.map((s, i) => (
            <div key={s.label} className={`flex justify-between gap-4 px-6 py-4 font-mono-accent text-sm ${i % 2 ? "bg-[#1F1F1F]" : ""}`}>
              <span className="text-[#7a7a7a] uppercase tracking-widest text-xs">{s.label}</span>
              <span className="text-[#F5F5F5] text-right">{s.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* RESEARCH INFORMATION */}
      <section className="border-y border-white/10 bg-[#1F1F1F]">
        <div className="mx-auto max-w-[1100px] px-5 sm:px-8 py-24">
          <Reveal><p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Research Information</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none">Scientific Reference</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
            {[
              { t: "Research Background", d: `${product.name} is studied within ${product.category.toLowerCase()} research as a defined ${product.tagline.toLowerCase()}. It is characterized for identity and purity to support reproducible experimental conditions.` },
              { t: "Mechanism of Interest", d: `Investigations focus on the compound's structural characteristics and interactions relevant to ${(product.research_areas || []).slice(0, 2).join(" and ") || "its target pathways"} in controlled laboratory models.` },
              { t: "Areas of Scientific Investigation", d: `Reported areas of interest include ${(product.research_areas || []).join(", ") || "biochemical characterization and assay development"}, examined strictly in vitro or in preclinical settings.` },
              { t: "Laboratory Applications", d: "Used as a reference material for assay development, analytical method validation, and comparative studies where a well-characterized compound is required." },
            ].map((b, i) => (
              <Reveal key={b.t} delay={(i % 2) * 0.06}>
                <h3 className="font-display font-bold text-2xl uppercase tracking-tight text-[#4ADE80]">{b.t}</h3>
                <p className="mt-3 text-[#E5E5E5] leading-relaxed">{b.d}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-12 font-mono-accent text-[11px] text-[#7a7a7a] leading-relaxed border-t border-white/10 pt-6">
            This information is provided for educational and research reference only. It does not constitute medical advice and makes no claim regarding the diagnosis, treatment, prevention, or cure of any condition. Products are for laboratory research use only and are not for human or veterinary use.
          </p>
        </div>
      </section>

      {/* WHY CHOOSE + QUALITY */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Why Choose Enda G1</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none">The Difference</h2>
          <ul className="mt-8 space-y-4">
            {reasons.map((r) => (
              <li key={r} className="flex items-start gap-3 text-[#E5E5E5]"><CircleCheck className="h-5 w-5 text-[#4ADE80] shrink-0 mt-0.5" /> <span className="leading-relaxed">{r}</span></li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Quality Assurance</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none">Our Process</h2>
          <div className="mt-8 space-y-5">
            {quality.map((q) => (
              <div key={q.n} className="flex gap-5">
                <span className="font-display font-black text-2xl text-[#4ADE80] w-10 shrink-0">{q.n}</span>
                <div><h4 className="font-display font-bold uppercase tracking-tight">{q.title}</h4><p className="text-[#A8A8A8] text-sm leading-relaxed mt-1">{q.desc}</p></div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="border-y border-white/10 bg-[#1F1F1F]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24">
          <Reveal><p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Customer Reviews</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter uppercase leading-none">What Labs Say</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Summary */}
            <div className="lg:sticky lg:top-24 lg:self-start border border-white/10 rounded-2xl p-8 text-center">
              <p className="font-display font-black text-6xl tracking-tighter text-[#4ADE80]">{rating.toFixed(1)}</p>
              <StarRating value={rating} size={20} className="justify-center mt-3" />
              <p className="mt-2 font-mono-accent text-xs text-[#7a7a7a]">{rcount} verified reviews</p>
              <div className="mt-6 space-y-2">
                {breakdown.map((b) => (
                  <div key={b.stars} className="flex items-center gap-2 font-mono-accent text-[11px] text-[#7a7a7a]">
                    <span className="w-3">{b.stars}</span>
                    <div className="flex-1 h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden"><div className="h-full bg-[#4ADE80]" style={{ width: `${(b.count / rcount) * 100}%` }} /></div>
                    <span className="w-8 text-right">{b.count}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Cards */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="reviews-list">
              {reviews.map((rv, i) => (
                <Reveal key={rv.id} delay={(i % 2) * 0.05} className="border border-white/10 rounded-2xl p-6 bg-[#0F0F10]">
                  <div className="flex items-center justify-between">
                    <StarRating value={rv.stars} size={14} />
                    {rv.verified && <span className="flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-[#4ADE80]"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
                  </div>
                  <h4 className="mt-3 font-display font-bold uppercase tracking-tight">{rv.title}</h4>
                  <p className="mt-2 text-[#A8A8A8] text-sm leading-relaxed">{rv.body}</p>
                  <div className="mt-4 flex items-center justify-between font-mono-accent text-[11px] text-[#7a7a7a]">
                    <span className="text-[#A8A8A8]">{rv.name}</span><span>{rv.date}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[900px] px-5 sm:px-8 py-24">
        <Reveal><p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">FAQ</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none">Questions, Answered</h2></Reveal>
        <Accordion type="single" collapsible className="mt-10 border-t border-white/10" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-white/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-bold uppercase tracking-tight text-base hover:text-[#4ADE80] hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#A8A8A8] leading-relaxed text-base pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FREQUENTLY BOUGHT TOGETHER */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <FrequentlyBought product={product} />
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24" data-testid="related-products">
            <Reveal><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-10">Customers Also Viewed</h2></Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F1F1F] border border-white/10 rounded-2xl overflow-hidden">
              {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* AI RECOMMENDATIONS + RECENTLY VIEWED */}
      <RecommendationRail title="You May Also Like" />
      <RecentlyViewed excludeSlug={product.slug} />

      {/* FINAL CTA */}
      <section className="border-t border-white/10 bg-[#1F1F1F]">
        <div className="mx-auto max-w-[1000px] px-5 sm:px-8 py-24 text-center">
          <Reveal>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter uppercase leading-none">Pharmaceutical-Grade, <span className="text-[#4ADE80]">Guaranteed</span></h2>
            <p className="mt-6 text-[#E5E5E5] text-lg leading-relaxed max-w-2xl mx-auto">Third-party tested, COA-backed, and shipped cold-chain from our lab to yours. Order {product.name} with secure checkout and fast domestic fulfillment.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 font-mono-accent text-xs uppercase tracking-widest text-[#A8A8A8]">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#4ADE80]" /> Third-Party Tested</span>
              <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#4ADE80]" /> Secure Checkout</span>
              <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#4ADE80]" /> Fast Shipping</span>
            </div>
            <button data-testid="final-add-to-cart" onClick={addToCart} className="mt-10 inline-flex items-center gap-2 btn-press bg-[#4ADE80] text-[#0F0F10] font-medium rounded-full px-10 py-5 rounded-full hover:bg-[#6FE998] hover:shadow-lg hover:shadow-[#4ADE80]/20 transition-[background-color,box-shadow] text-lg">
              <ShoppingBag className="h-5 w-5" /> Add to Cart — {fmtPrice(unit)}
            </button>
          </Reveal>
        </div>
      </section>

      <Footer />

      {/* STICKY MOBILE ADD-TO-CART */}
      <div data-testid="sticky-cart-bar" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-black/90 backdrop-blur-xl border-t border-white/10 px-4 py-3 flex items-center gap-3">
        <div className="min-w-0">
          <p className="font-display font-bold text-sm uppercase tracking-tight truncate">{product.name}</p>
          <p className="font-mono-accent text-[#4ADE80] text-sm">{fmtPrice(unit)}</p>
        </div>
        <button onClick={buyNow} className="ml-auto shrink-0 border border-[#4ADE80] text-[#4ADE80] font-display font-bold uppercase tracking-tight px-4 py-3 rounded-full text-xs">Buy Now</button>
        <button data-testid="sticky-add-to-cart" onClick={addToCart} className="shrink-0 btn-press bg-[#4ADE80] text-[#0F0F10] font-medium rounded-full px-5 py-3 rounded-full text-xs flex items-center gap-1"><ShoppingBag className="h-4 w-4" /> Add</button>
      </div>
    </div>
  );
}
